<template>
  <v-card class="elevation-0 mt-8">
    <v-card-title class="text-h6 font-weight-bold d-flex align-center justify-space-between">
      <div class="d-flex align-center justify-space-between">
        <span class="pr-2">{{ $vuetify.lang.t("$vuetify.ask_maira_access.guest_url_title_txt") }}</span>
        <v-tooltip bottom max-width="350">
          <template v-slot:activator="{ on, attrs }">
            <span v-on="on" v-bind="attrs" class="info-icon">i</span>
          </template>
          <span>{{ $vuetify.lang.t("$vuetify.ask_maira_access.guest_url_hint_txt") }}</span>
        </v-tooltip>
      </div>
    </v-card-title>
    <v-divider></v-divider>

    <v-card class="elevation-0">
      <v-row class="mx-2 mb-3">
        <v-col cols="12" md="6">
          <v-card class="outlined pa-2" flat>
            <strong>
              {{ $vuetify.lang.t("$vuetify.ask_maira_access.all_direct_links_txt") }}
            </strong>
          </v-card>
        </v-col>
        <v-col cols="12" md="6">
          <v-row class="justify-end mt-1">
            <v-select
              class="access-type-selector text-uppercase d-inline-block mr-2"
              :items="directLinkAccessTypes"
              placeholder="ACCESS TYPE"
              item-text="text"
              item-value="value"
              v-model="directLinkAccessType"
              dense
              outlined
              hide-details
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || isLoadingAccessTypes"
              :menu-props="{
                bottom: true,
                offsetY: true,
              }"
              @change="handleAccessTypeChange"
            >
              <template #item="{ item }">
                <div class="d-flex align-center justify-space-between w-100">
                  <span>{{ item.text }}</span>

                  <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                      <v-icon small class="ml-3" v-bind="attrs" v-on="on" @click.stop> mdi-information-outline </v-icon>
                    </template>

                    <span>{{ item.description }}</span>
                  </v-tooltip>
                </div>
              </template>
            </v-select>
            <Button
              :button-type="FLOATING_ACTION_BUTTON"
              icon-name="mdi-plus"
              size="x-small"
              :button-text="$vuetify.lang.t('$vuetify.ask_maira_access.create_direct_link_btn_txt')"
              color="primary"
              :disabled="!$hasProjectAccessFor([$pRole.ADMIN]) || isLoading"
              @onClick="openDirectLinkModal()"
            />
          </v-row>
        </v-col>
      </v-row>
    </v-card>
    <v-divider></v-divider>
    <Loader v-if="isLoading" class="mr-2 mt-6" />

    <v-data-table
      v-else
      class="elevation-1 mt-6"
      :headers="directLinkHeaders"
      :items="directLinks"
      :sort-by="['created_at']"
      :sort-desc="[true]"
      :loading="directLinkModalLoading"
      :loading-text="$vuetify.lang.t('$vuetify.common.loading')"
      outlined
    >
      <template #[`item.id`]="{ item }">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">{{ item.id.split("-")[0] }}</span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>

      <template #[`item.created_at`]="{ item }">
        {{ item.created_at ? formatDateTime(item.created_at) : "N/A" }}
      </template>

      <template #[`item.name`]="{ item }">
        <TruncateTooltip :text="item.name" :limit="30" :tooltip-only-when-truncated="true" />
      </template>

      <template #[`item.profile`]="{ item }">
        <TruncateTooltip :text="item.profile" :limit="30" :tooltip-only-when-truncated="true" />
      </template>

      <template #[`item.client`]="{ item }">
        <TruncateTooltip :text="item.client" :limit="30" :tooltip-only-when-truncated="true" />
      </template>

      <template #item.actions="{ item }">
        <div class="direct-link-actions">
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                v-on="on"
                @click="editDirectLink(item)"
              >
                <v-icon small>mdi-pencil</v-icon>
              </v-btn>
            </template>
            <span>{{ $vuetify.lang.t("$vuetify.common.edit_btn") }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                v-if="item.status !== 'inactive'"
                icon
                small
                v-bind="attrs"
                :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                v-on="on"
                @click="changeDirectLinkStatus(item, 'inactive')"
              >
                <v-icon small>mdi-stop</v-icon>
              </v-btn>
              <v-btn
                v-else
                icon
                small
                v-bind="attrs"
                :disabled="!$hasProjectAccessFor([$pRole.ADMIN])"
                v-on="on"
                @click="changeDirectLinkStatus(item, 'active')"
              >
                <v-icon small>mdi-reload</v-icon>
              </v-btn>
            </template>
            <span>{{
              item.status === "inactive"
                ? $vuetify.lang.t("$vuetify.ask_maira_access.start_btn_txt")
                : $vuetify.lang.t("$vuetify.ask_maira_access.stop_btn_txt")
            }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  icon
                  small
                  :disabled="!item.direct_link || item.profileMissing || item.clientKeyMissing"
                  @click="copyToClipboard(item.direct_link)"
                >
                  <v-icon small>mdi-content-copy</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{
              item.profileMissing || item.clientKeyMissing
                ? getMissingActionMessage(item)
                : $vuetify.lang.t("$vuetify.ask_maira_access.copy")
            }}</span>
          </v-tooltip>

          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <span v-bind="attrs" v-on="on">
                <v-btn
                  icon
                  small
                  rel="noopener noreferrer"
                  :disabled="!item.direct_link || item.profileMissing || item.clientKeyMissing"
                  :href="item.direct_link"
                  target="_blank"
                >
                  <v-icon small>mdi-open-in-new</v-icon>
                </v-btn>
              </span>
            </template>
            <span>{{
              item.profileMissing || item.clientKeyMissing
                ? getMissingActionMessage(item)
                : $vuetify.lang.t("$vuetify.ask_maira_access.visit_link_tooltip_txt")
            }}</span>
          </v-tooltip>
        </div>
      </template>

      <template #no-data>
        {{ $vuetify.lang.t("$vuetify.ask_maira_access.no_direct_links_txt") }}
      </template>
    </v-data-table>
    <!-- <v-divider></v-divider> -->
    <v-card class="elevation-0 mt-1">
      <v-row class="mx-2 mb-3">
        <v-col cols="12" md="6">
          <v-card class="outlined pa-2" flat>
            <strong>
              {{ $vuetify.lang.t("$vuetify.ask_maira_access.direct_link_users") }}
            </strong>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
    <v-divider></v-divider>

    <v-row class="d-flex flex-column flex-md-row py-4 mb-n6 px-6">
      <v-col cols="12" md="4" class="pa-0 pr-0 p-md-3">
        <v-text-field
          v-model="directLinkUsersPagination.search"
          :label="$vuetify.lang.t('$vuetify.gpt_history.search_txt')"
          outlined
          dense
          clearable
          append-icon="mdi-magnify"
        ></v-text-field>
      </v-col>
    </v-row>
    <v-divider></v-divider>

    <Loader v-if="isLoadingUserList" class="mr-2 mt-6" />
    <v-data-table
      v-else
      class="elevation-1"
      :headers="directLinkUsersHeaders"
      :items="directLinkUsers"
      :server-items-length="directLinkUsersTotal"
      :page="directLinkUsersPagination.page"
      :items-per-page="directLinkUsersPagination.itemsPerPage"
      :sort-by="directLinkUsersPagination.sortBy"
      :sort-desc="directLinkUsersPagination.sortDesc"
      :loading="isLoadingUserList"
      :loading-text="$vuetify.lang.t('$vuetify.common.loading')"
      :footer-props="{
        'items-per-page-options': [5, 10, 15],
      }"
      outlined
      @update:page="handleDirectLinkPageChange"
      @update:items-per-page="handleDirectLinkItemsPerPageChange"
      @update:sort-by="handleDirectLinkSortByChange"
      @update:sort-desc="handleDirectLinkSortDescChange"
    >
      <template #[`item.id`]="{ item }">
        <v-tooltip top>
          <template #activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">{{ item.id ? item.id.split("-")[0] : "N/A" }}</span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>

      <template #[`item.created_at`]="{ item }">
        {{ item.created_at ? formatDateTime(item.created_at) : "N/A" }}
      </template>

      <template #[`item.username`]="{ item, isMobile }">
        <span v-if="isMobile" class="direct-link-username-mobile">{{ item.username || "N/A" }}</span>
        <TruncateTooltip v-else :text="item.username" :limit="30" :tooltip-only-when-truncated="true" />
      </template>
      <template #[`item.actions`]="{ item }">
        <div class="d-flex justify-center align-center">
          <span class="d-inline-block">
            <v-btn
              small
              outlined
              color="blue-grey lighten-2"
              class="primary--text reset-btn"
              @click="openResetPasswordModal(item)"
            >
              {{ $vuetify.lang.t("$vuetify.ask_maira_access.reset_password") }}
            </v-btn>
          </span>
        </div>
      </template>
    </v-data-table>

    <v-dialog v-model="credentialDialog" width="500" persistent>
      <v-card>
        <div class="px-6 py-8 primary--text font-weight-bold">
          {{ $vuetify.lang.t("$vuetify.ask_maira_access.credential_required_msg_txt") }}
        </div>
        <v-divider></v-divider>

        <v-form ref="credentialForm" class="px-6 pt-8" @submit.prevent="saveCredentialClick">
          <v-layout>
            <v-row>
              <v-col cols="12" md="12">
                <v-text-field
                  v-model="credentialName"
                  :label="$vuetify.lang.t('$vuetify.project_credentials.credentials_name_header_txt')"
                  :rules="[v => !!v || $vuetify.lang.t('$vuetify.project_credentials.credentials_error_msg_txt')]"
                  outlined
                  dense
                ></v-text-field>
              </v-col>
            </v-row>
          </v-layout>
        </v-form>

        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text class="red--text font-weight-bold" color="red" @click="closeCredentialDialog">
            {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
          </v-btn>
          <v-btn
            class="font-weight-bold"
            :disabled="!credentialFormValid"
            :color="credentialFormValid ? 'primary' : '#90A4AE'"
            :loading="credentialSaving"
            @click="saveCredentialClick"
          >
            {{ $vuetify.lang.t("$vuetify.common.create") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="successDialog" persistent width="600" @keydown.esc="handleDownloadModalClose">
      <v-card>
        <v-card-title class="headline white--text text--primary mb-4">
          {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_label_txt") }}
        </v-card-title>
        <v-card-text>
          <div class="mb-8">
            <v-alert type="info" outlined>
              {{ $vuetify.lang.t("$vuetify.project_credentials.credentials_download_json_msg_txt") }}
            </v-alert>
          </div>

          <v-text-field
            v-model="generatedClientKey"
            :label="$vuetify.lang.t('$vuetify.project_credentials.client_key_label_txt')"
            outlined
            readonly
          >
            <template #append>
              <v-tooltip top>
                <template #activator="{ on }">
                  <v-icon v-on="on" @click="copyClientKey()">mdi-content-copy</v-icon>
                </template>
                {{
                  clientKeyCopied
                    ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                    : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                }}
              </v-tooltip>
            </template>
          </v-text-field>

          <v-btn color="primary" @click="downloadCredential()">
            <v-icon>mdi-download</v-icon>
            {{ $vuetify.lang.t("$vuetify.project_credentials.download_json_msg_txt") }}
          </v-btn>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text color="primary" outlined @click="handleDownloadModalClose">
            {{ $vuetify.lang.t("$vuetify.ask_maira_access.create_guest_url_title_txt") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="directLinkDialog" persistent width="60%">
      <v-card class="pa-8">
        <v-skeleton-loader v-if="directLinkModalLoading" type="article, actions"></v-skeleton-loader>
        <template v-else>
          <v-row>
            <v-col>
              <div class="py-4 primary--text font-weight-bold">
                {{
                  editDirectLinkMode
                    ? $vuetify.lang.t("$vuetify.ask_maira_access.update_guest_url_title_txt")
                    : $vuetify.lang.t("$vuetify.ask_maira_access.create_guest_url_title_txt")
                }}
              </div>
            </v-col>
          </v-row>

          <v-form ref="direct_link_form" lazy-validation>
            <v-row>
              <v-col>
                <v-text-field
                  v-model="title"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                  :label="$vuetify.lang.t('$vuetify.ask_maira_access.title_label_txt')"
                  :placeholder="$vuetify.lang.t('$vuetify.ask_maira_access.title_placeholder_txt')"
                  :rules="[rules.requiredField($vuetify.lang.t('$vuetify.ask_maira_access.title_label_txt'))]"
                  :disabled="isLoading"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="clientKey"
                  :items="clientKeyOptions"
                  item-text="name"
                  item-value="value"
                  :label="$vuetify.lang.t('$vuetify.project_credentials.client_key_label_txt')"
                  :placeholder="$vuetify.lang.t('$vuetify.ask_maira_access.select_client_key_placeholder_txt')"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                  :rules="[
                    rules.requiredField($vuetify.lang.t('$vuetify.project_credentials.client_key_label_txt')),
                    rules.clientKeyActive,
                  ]"
                  :loading="clientKeysLoading"
                  :disabled="clientKeysLoading || isLoading"
                  :no-data-text="
                    clientKeysFetchFailed
                      ? $vuetify.lang.t('$vuetify.ask_maira_access.client_keys_load_failed_txt')
                      : $vuetify.lang.t('$vuetify.ask_maira_access.no_client_keys_found_txt')
                  "
                >
                  <template #item="{ item }">
                    <v-tooltip bottom max-width="350" :disabled="!dropdownTooltip(item.name)">
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="dropdown-item-text">
                          {{ truncateDropdownText(item.name) }}
                        </span>
                      </template>

                      <span>{{ item.name }}</span>
                    </v-tooltip>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-autocomplete
                  v-model="selectedProfileId"
                  :items="profileOptions"
                  item-text="name"
                  item-value="id"
                  :label="$vuetify.lang.t('$vuetify.ask_maira_access.choose_profile_label_txt')"
                  :placeholder="$vuetify.lang.t('$vuetify.ask_maira_access.select_profile_placeholder_txt')"
                  dense
                  outlined
                  clearable
                  hide-details="auto"
                  :rules="[rules.requiredField($vuetify.lang.t('$vuetify.ask_maira_access.choose_profile_label_txt'))]"
                  :loading="profilesLoading"
                  :disabled="profilesLoading || isLoading"
                  :no-data-text="
                    profileFetchFailed
                      ? $vuetify.lang.t('$vuetify.ask_maira_access.profiles_load_failed_txt')
                      : $vuetify.lang.t('$vuetify.ask_maira_access.no_profiles_found_txt')
                  "
                >
                  <template #item="{ item }">
                    <v-tooltip bottom max-width="350" :disabled="!dropdownTooltip(item.name)">
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" class="dropdown-item-text">
                          {{ truncateDropdownText(item.name) }}
                        </span>
                      </template>

                      <span>{{ item.name }}</span>
                    </v-tooltip>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row>
              <v-col>
                <v-select
                  v-model="defaultLanguage"
                  :items="defaultLanguageOptions"
                  item-text="name"
                  item-value="value"
                  :label="$vuetify.lang.t('$vuetify.ask_maira_access.default_language_label_txt')"
                  :placeholder="$vuetify.lang.t('$vuetify.ask_maira_access.select_default_language_placeholder_txt')"
                  dense
                  outlined
                  hide-details="auto"
                  :rules="[
                    rules.requiredField($vuetify.lang.t('$vuetify.ask_maira_access.default_language_label_txt')),
                  ]"
                  :disabled="isLoading"
                />
              </v-col>
            </v-row>
          </v-form>

          <v-row>
            <v-col>
              <v-card-actions class="justify-start justify-sm-end pa-0">
                <v-btn color="error" outlined @click="closeDirectLinkModal">
                  {{ $vuetify.lang.t("$vuetify.common.cancel_btn") }}
                </v-btn>

                <v-btn
                  color="primary"
                  outlined
                  :loading="isLoading"
                  :disabled="isDirectLinkSubmitDisabled"
                  @click="() => (editDirectLinkMode ? updateDirectLink() : createDirectLink())"
                >
                  {{
                    editDirectLinkMode
                      ? $vuetify.lang.t("$vuetify.ask_maira_access.update")
                      : $vuetify.lang.t("$vuetify.common.create")
                  }}
                  <v-progress-circular v-if="isLoading" indeterminate color="white"></v-progress-circular>
                </v-btn>
              </v-card-actions>
            </v-col>
          </v-row>
        </template>
      </v-card>
    </v-dialog>
    <v-dialog v-model="resetPasswordDialog" persistent width="600" max-width="calc(100% - 32px)">
      <v-card>
        <v-card-title class="headline text--primary mb-4">
          {{ $vuetify.lang.t("$vuetify.ask_maira_access.new_password") }}
        </v-card-title>

        <v-card-text>
          <!-- Loading -->
          <template v-if="resetPasswordLoading">
            <v-skeleton-loader type="paragraph" class="mb-4" />

            <v-skeleton-loader type="text" height="56" class="mb-4" />

            <v-skeleton-loader type="button" width="180" />
          </template>

          <!-- Result -->
          <template v-else>
            <div class="mb-4">
              <v-alert type="info" outlined class="reset-password-alert">
                {{ $vuetify.lang.t("$vuetify.ask_maira_access.reset_password_warning") }}
              </v-alert>
            </div>
            <v-text-field
              outlined
              readonly
              :value="selectedResetPasswordUser ? selectedResetPasswordUser.username : ''"
              :label="$vuetify.lang.t('$vuetify.ask_maira_access.username')"
              class="reset-password-field mb-4"
            />

            <v-text-field
              outlined
              readonly
              :value="resetPasswordData ? resetPasswordData.new_password : ''"
              :label="$vuetify.lang.t('$vuetify.ask_maira_access.new_password')"
              class="reset-password-field"
            >
              <template #append>
                <v-tooltip top>
                  <template #activator="{ on }">
                    <v-icon v-on="on" @click="copyResetPassword"> mdi-content-copy </v-icon>
                  </template>

                  <span>
                    {{
                      reset_password_copied
                        ? $vuetify.lang.t("$vuetify.common.copied_msg_txt")
                        : $vuetify.lang.t("$vuetify.common.copy_msg_txt")
                    }}
                  </span>
                </v-tooltip>
              </template>
            </v-text-field>

            <div class="mt-4">
              <v-btn
                color="primary"
                small
                class="download-password-btn"
                @click="downloadResetPassword"
                :disabled="!resetPasswordData"
              >
                <v-icon left>mdi-download</v-icon>

                {{ $vuetify.lang.t("$vuetify.project_credentials.download_json_msg_txt") }}
              </v-btn>
            </div>
          </template>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions>
          <v-spacer></v-spacer>

          <v-btn color="error" outlined @click="closeResetPasswordDialog" :disabled="resetPasswordLoading">
            {{ $vuetify.lang.t("$vuetify.common.close_btn") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <DialogModal
      v-model="resetPasswordConfirmDialog"
      :title="$vuetify.lang.t('$vuetify.ask_maira_access.reset_password')"
      :message="$vuetify.lang.t('$vuetify.ask_maira_access.reset_password_confirm_message')"
      :confirm-button-text="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :cancel-button-text="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      @onConfirmation="confirmResetPassword"
      @onCancel="resetPasswordConfirmDialog = false"
    />
    <DialogModal
      v-model="accessTypeConfirmDialog"
      :title="$vuetify.lang.t('$vuetify.common.confirm_alert_title_txt')"
      :message="$vuetify.lang.t('$vuetify.ask_maira_access.change_access_type_confirm_message')"
      :confirm-button-text="$vuetify.lang.t('$vuetify.common.confirm_btn')"
      :confirm-btn-loading="isLoadingAccessTypes"
      :cancel-button-text="$vuetify.lang.t('$vuetify.common.cancel_btn')"
      @onConfirmation="onDirectLinkAccessTypeChange"
      @onCancel="cancelDirectLinkAccessTypeChange"
    />
  </v-card>
</template>

<script>
import { mapActions, mapState } from "vuex";

import { FLOATING_ACTION_BUTTON } from "@/components/common/button/_buttontypes";
import Button from "@/components/common/button/Button";
import Loader from "@/components/common/loader/Loader";
import TruncateTooltip from "@/components/common/tooltip/TruncateTooltip";
import getEnv from "@/config/env";
import { copyTextToClipboard, formatDateTime } from "@/helpers/helper";
import {
  ADD_ALERT,
  CREATE_GUEST_LINK,
  GET_GPT_PROFILES,
  GET_GUEST_LINKS,
  GET_GUEST_LINKS_USERS,
  RESET_GUEST_LINK_USER_PASSWORD,
  UPDATE_PROJECT_DIRECT_LINK_ACCESS_TYPE,
  LOAD_ALL_CREDENTIAL,
  SAVE_CREDENTIAL,
  UPDATE_GUEST_LINK,
} from "@/store/_actiontypes";
import DialogModal from "@/components/common/DialogModal";
import { getApiErrorMessage } from "../../helpers/apiError";

const DEFAULT_LANGUAGE = "ja";

export default {
  name: "AskMairaAccess",
  components: {
    // eslint-disable-next-line vue/no-reserved-component-names
    Button,
    Loader,
    TruncateTooltip,
    DialogModal,
  },
  data() {
    return {
      FLOATING_ACTION_BUTTON: FLOATING_ACTION_BUTTON,

      isLoading: false,
      directLinkDialog: false,
      editDirectLinkMode: false,

      title: "",
      clientKey: "",
      selectedProfileId: null,
      defaultLanguage: DEFAULT_LANGUAGE,
      selectedDirectLink: {},

      directLinks: [],

      clientKeysLoading: false,
      clientKeysFetchFailed: false,
      credentialsLoaded: false,
      profilesLoading: false,
      profileFetchFailed: false,
      profilesLoaded: false,

      credentialDialog: false,
      credentialName: "",
      credentialType: "client",
      whitelistedDomainShow: [],
      whitelistedDomain: {},
      isValidWhitelistedDomain: true,
      credentialSaving: false,
      successDialog: false,
      generatedClientKey: "",
      clientKeyCopied: false,
      openDirectLinkAfterCredential: false,

      MairaAppUrl: "",
      formatDateTime,

      rules: {
        requiredField: fieldName => value =>
          (!!value && !!String(value).trim()) ||
          this.$vuetify.lang.t("$vuetify.common.field_required_named_error_txt", [fieldName]),
        clientKeyActive: value =>
          this.clientKeyOptions.some(option => option.value === value) ||
          this.$vuetify.lang.t("$vuetify.common.field_required_error_txt"),
      },

      directLinkHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.id"),
          value: "id",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.name"),
          value: "name",
          align: "start",
          sortable: true,
          width: "30%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.profile_header_txt"),
          value: "profile",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.client_header_txt"),
          value: "client",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.default_language_header"),
          value: "default_language_name",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.created_at_header_txt"),
          value: "created_at",
          align: "start",
          sortable: true,
          width: "250px",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.common.status"),
          value: "status",
          align: "start",
          sortable: true,
          width: "50px",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.common.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
      directLinkUsersHeaders: [
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.id"),
          value: "id",
          align: "start",
          sortable: true,
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.username"),
          value: "username",
          align: "start",
          sortable: true,
          width: "30%",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.ask_maira_access.created_at_header_txt"),
          value: "created_at",
          align: "start",
          sortable: true,
          width: "280px",
        },
        {
          text: this.$vuetify.lang.t("$vuetify.common.actions"),
          value: "actions",
          align: "center",
          sortable: false,
        },
      ],
      directLinkAccessType: null,
      previousDirectLinkAccessType: "optional",
      directLinkAccessTypes: [
        {
          text: "REQUIRED",
          value: "required",
          description: this.$vuetify.lang.t("$vuetify.ask_maira_access.required_direct_access_link"),
        },
        {
          text: "OPTIONAL",
          value: "optional",
          description: this.$vuetify.lang.t("$vuetify.ask_maira_access.optional_direct_access_link"),
        },
        {
          text: "DISABLED",
          value: "disabled",
          description: this.$vuetify.lang.t("$vuetify.ask_maira_access.disabled_direct_access_link"),
        },
      ],
      isLoadingAccessTypes: false,
      selectedUser: null,
      resetPasswordUserIdCopied: false,

      directLinkUsers: [],
      directLinkUsersTotal: 0,
      directLinkUsersRequestId: 0,
      directLinkUsersPagination: {
        page: 1,
        itemsPerPage: 10,
        sortBy: ["created_at"],
        sortDesc: [true],
        search: "",
      },
      isLoadingUserList: false,
      searchTimer: null,
      resetPasswordDialog: false,
      resetPasswordData: null,
      resetPasswordLoading: false,
      reset_password_copied: false,
      resetPasswordConfirmDialog: false,
      selectedResetPasswordUser: null,
      accessTypeConfirmDialog: false,
      pendingDirectLinkAccessType: null,
    };
  },
  computed: {
    ...mapState({
      selectedProject: state => state.project.selectedProject,
      allCredential: state => state.project.allCredential,
      gptProfiles: state => state.project.gptProfiles,
      guestLinkAuthMode: state => state.project.selectedProject?.guest_link_auth_mode,
    }),
    projectId() {
      return this.selectedProject?.id || this.$route.query.pid;
    },
    dropdownItemNameLimit() {
      return this.$vuetify.breakpoint.mdAndUp ? 80 : 16;
    },
    profileOptions() {
      return (this.gptProfiles.profiles || []).map(profile => ({
        id: profile.id,
        name: profile.name || profile.id,
      }));
    },
    defaultLanguageOptions() {
      return [
        {
          name: this.$vuetify.lang.t("$vuetify.ask_maira_access.language_en_txt"),
          value: "en",
        },
        {
          name: this.$vuetify.lang.t("$vuetify.ask_maira_access.language_ja_txt"),
          value: "ja",
        },
      ];
    },
    allClientKeyOptions() {
      return this.allCredential
        .filter(credential => this.isClientCredential(credential))
        .map((credential, index) => this.mapClientKeyOption(credential, index))
        .filter(option => option.value);
    },
    clientKeyOptions() {
      return this.allClientKeyOptions.filter(option => option.status !== "inactive");
    },
    credentialFormValid: {
      get() {
        return this.credentialName && this.credentialName.trim() !== "" && this.isValidWhitelistedDomain;
      },
      set() {},
    },
    isDirectLinkSubmitDisabled() {
      return (
        !this.title ||
        !this.title.trim() ||
        !this.clientKey ||
        !this.selectedProfileId ||
        !this.defaultLanguage ||
        this.clientKeysLoading ||
        this.profilesLoading ||
        this.isLoading
      );
    },
    directLinkModalLoading() {
      return this.clientKeysLoading || this.profilesLoading;
    },
  },
  mounted() {
    this.loadEnvs();
    this.getDirectLinks();
    this.getDirectLinkUsers();
  },

  watch: {
    "directLinkUsersPagination.search"() {
      clearTimeout(this.searchTimer);

      this.searchTimer = setTimeout(() => {
        this.directLinkUsersPagination.page = 1;
        this.getDirectLinkUsers();
      }, 500);
    },
    guestLinkAuthMode: {
      immediate: true,
      handler(value) {
        this.directLinkAccessType = value || "optional";
        this.previousDirectLinkAccessType = value || "optional";
      },
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer);
  },
  methods: {
    ...mapActions("project", [
      GET_GPT_PROFILES,
      GET_GUEST_LINKS,
      GET_GUEST_LINKS_USERS,
      RESET_GUEST_LINK_USER_PASSWORD,
      UPDATE_PROJECT_DIRECT_LINK_ACCESS_TYPE,
      LOAD_ALL_CREDENTIAL,
      CREATE_GUEST_LINK,
      SAVE_CREDENTIAL,
      UPDATE_GUEST_LINK,
    ]),
    ...mapActions("alert", [ADD_ALERT]),

    loadEnvs() {
      this.MairaAppUrl = getEnv("VUE_APP_MAIRA_APP_URL");
    },

    async openDirectLinkModal() {
      this.resetDirectLinkForm();

      if (!this.credentialsLoaded) {
        await this.getProjectClientKeys();
      }

      if (!this.clientKeyOptions.length) {
        this.openDirectLinkAfterCredential = true;
        this.openCredentialDialog();
        return;
      }

      this.directLinkDialog = true;

      if (!this.profilesLoaded) {
        this.getGptProfiles();
      }

      this.$nextTick(() => {
        this.$refs.direct_link_form?.resetValidation();
      });
    },

    closeDirectLinkModal() {
      this.directLinkDialog = false;
      this.resetDirectLinkForm();

      this.$nextTick(() => {
        this.$refs.direct_link_form?.resetValidation();
      });
    },

    resetDirectLinkForm() {
      this.editDirectLinkMode = false;
      this.title = "";
      this.clientKey = "";
      this.selectedProfileId = null;
      this.defaultLanguage = DEFAULT_LANGUAGE;
      this.selectedDirectLink = {};
    },

    openCredentialDialog() {
      if (!this.selectedProject) {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.project_not_loaded"),
          color: "error",
        });
        return;
      }
      if (this.selectedProject.status === "running") {
        this.resetCredentialForm();
        this.credentialDialog = true;

        this.$nextTick(() => {
          this.$refs.credentialForm?.resetValidation();
        });
      } else if (this.selectedProject.status === "locked") {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.project_locked"),
          color: "error",
        });
      } else {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.project_status_not_allowed"),
          color: "error",
        });
      }
    },

    closeCredentialDialog() {
      this.credentialDialog = false;
      this.openDirectLinkAfterCredential = false;
      this.resetCredentialForm();
    },

    resetCredentialForm() {
      this.credentialName = "";
      this.credentialType = "client";
      this.whitelistedDomainShow = [];
      this.whitelistedDomain = {};
      this.isValidWhitelistedDomain = true;
      this.generatedClientKey = "";
      this.clientKeyCopied = false;
    },

    validateWhitelistedDomain() {
      return () => {
        const domains = Array.isArray(this.whitelistedDomainShow) ? this.whitelistedDomainShow : [];

        if (("project" === this.credentialType || "client" === this.credentialType) && domains.length === 0) {
          this.isValidWhitelistedDomain = true;
          return true;
        }

        const domainPattern =
          /^(?!:\/\/)(?=.{1,255}$)(?:(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,63}|localhost|\b((25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[0-1]?[0-9][0-9]?)\b)$/;
        const isValid = domains.every(domain => domainPattern.test(domain));

        this.isValidWhitelistedDomain = isValid;

        return isValid || this.$vuetify.lang.t("$vuetify.project_credentials.whitelisted_domain_validation_txt");
      };
    },

    getMairaWhitelistDomain() {
      if (getEnv("NODE_ENV") === "development") return "";

      try {
        return new URL(this.MairaAppUrl).hostname;
      } catch (error) {
        return "";
      }
    },

    saveCredentialClick() {
      if (!this.credentialFormValid || !this.isValidWhitelistedDomain) return;

      const mairaDomain = this.getMairaWhitelistDomain();
      const domains = mairaDomain ? [mairaDomain] : [];

      this.whitelistedDomain = {
        whitelists: domains,
      };

      if (!this.projectId) return;

      this.saveCredential({
        project_id: this.projectId,
        name: this.credentialName,
        type: this.credentialType,
        whitelisted: this.whitelistedDomain,
      });
    },

    async saveCredential(payload) {
      this.credentialSaving = true;

      try {
        const response = await this.SAVE_CREDENTIAL(payload);
        const credentialDetails = response?.data?.details || {};

        this.generatedClientKey = credentialDetails.client_key || "";

        await this.getProjectClientKeys();

        this.selectCreatedClientKey();
        this.credentialDialog = false;
        this.successDialog = true;
      } catch (error) {
        this.ADD_ALERT({
          message: error.response?.data?.message || this.$vuetify.lang.t("$vuetify.ask_maira_access.unexpected_error"),
          color: "error",
        });
      } finally {
        this.credentialSaving = false;
      }
    },

    selectCreatedClientKey() {
      const createdCredential =
        this.clientKeyOptions.find(option => option.name === this.credentialName) || this.clientKeyOptions[0];

      this.clientKey = createdCredential?.value || "";
    },

    downloadCredential() {
      const jsonObject = { client_key: this.generatedClientKey };
      const credentialName = this.credentialName || "client-key";
      const fileName = `${
        credentialName.indexOf(" ") >= 0 ? credentialName.trim().replace(/\s/g, "-") : credentialName
      }-${this.projectId}.json`;

      if (!window.navigator.msSaveOrOpenBlob) {
        const url = window.URL.createObjectURL(new Blob([JSON.stringify(jsonObject)]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        window.navigator.msSaveOrOpenBlob(new Blob([JSON.stringify(jsonObject)]), fileName);
      }
    },

    copyClientKey() {
      if (!navigator.clipboard) {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.clipboard_not_available"),
          color: "error",
        });
        return;
      }
      navigator.clipboard.writeText(this.generatedClientKey).then(
        () => {
          this.clientKeyCopied = true;
          setTimeout(() => {
            this.clientKeyCopied = false;
          }, 2000);
        },
        () => {
          this.ADD_ALERT({
            message: this.$vuetify.lang.t("$vuetify.ask_maira_access.copy_client_key_failed"),
            color: "error",
          });
        }
      );
    },

    handleDownloadModalClose() {
      this.successDialog = false;

      if (this.openDirectLinkAfterCredential) {
        this.openDirectLinkAfterCredential = false;
        this.directLinkDialog = true;

        if (!this.profilesLoaded) {
          this.getGptProfiles();
        }

        this.$nextTick(() => {
          this.$refs.direct_link_form?.resetValidation();
        });
      }
    },

    async editDirectLink(item) {
      this.editDirectLinkMode = true;
      this.directLinkDialog = true;
      this.selectedDirectLink = item;
      this.title = item.name || "";
      this.clientKey = item.project_key_id || item.client_key || "";
      this.selectedProfileId = item.profile_id || null;
      this.defaultLanguage = item.default_language || DEFAULT_LANGUAGE;

      if (!this.credentialsLoaded) {
        await this.getProjectClientKeys();
      }
      if (!this.profilesLoaded) {
        await this.getGptProfiles();
      }

      this.$nextTick(() => {
        this.$refs.direct_link_form?.resetValidation();
      });
    },

    changeDirectLinkStatus(item, status) {
      this.selectedDirectLink = item;
      this.title = item.name || "";
      this.clientKey = item.project_key_id || item.client_key || "";
      this.selectedProfileId = item.profile_id || null;
      this.defaultLanguage = item.default_language || DEFAULT_LANGUAGE;
      this.updateDirectLink(status);
    },

    async getDirectLinks() {
      if (!this.projectId) {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.project_id_missing"),
          color: "error",
        });
        return;
      }

      this.isLoading = true;

      try {
        if (!this.credentialsLoaded) {
          await this.getProjectClientKeys();
        }
        if (!this.profilesLoaded) {
          await this.getGptProfiles();
        }

        const response = await this.GET_GUEST_LINKS({
          project_id: this.projectId,
        });

        const guestLinks = response?.data?.guest_links || [];
        this.directLinks = guestLinks.map(item => this.mapGuestLink(item));
      } catch (error) {
        this.directLinks = [];
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.no_direct_links_txt"),
          color: "error",
        });
      } finally {
        this.isLoading = false;
      }
    },

    async getDirectLinkUsers() {
      if (!this.projectId) {
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.project_id_missing"),
          color: "error",
        });
        return;
      }
      const requestId = ++this.directLinkUsersRequestId;
      try {
        this.isLoadingUserList = true;

        const { page, itemsPerPage, sortBy, sortDesc, search } = this.directLinkUsersPagination;

        const response = await this[GET_GUEST_LINKS_USERS]({
          project_id: this.projectId,
          page,
          per_page: itemsPerPage,
          search: search || undefined,
          sort_by: sortBy?.[0] || "created_at",
          sort_order: sortDesc?.[0] ? "desc" : "asc",
        });

        // Only the latest request may update the table.
        if (requestId !== this.directLinkUsersRequestId) return;

        const data = response?.data?.detail?.response || response?.data || {};

        this.directLinkUsers = data.items || [];
        this.directLinkUsersTotal = Number(data.total) || 0;
      } catch (error) {
        if (requestId !== this.directLinkUsersRequestId) return;

        console.error("Failed to fetch direct link users:", error);

        this.directLinkUsers = [];
        this.directLinkUsersTotal = 0;
      } finally {
        if (requestId === this.directLinkUsersRequestId) {
          this.isLoadingUserList = false;
        }
      }
    },

    getProjectClientKeys() {
      this.clientKeysLoading = true;
      this.clientKeysFetchFailed = false;

      return this.LOAD_ALL_CREDENTIAL({
        project_id: this.projectId,
      })
        .then(() => {
          this.credentialsLoaded = true;
        })
        .catch(() => {
          this.clientKeysFetchFailed = true;
        })
        .finally(() => {
          this.clientKeysLoading = false;
        });
    },

    getGptProfiles() {
      this.profilesLoading = true;
      this.profileFetchFailed = false;

      return this.GET_GPT_PROFILES({
        project_id: this.projectId,
        start: 0,
        size: 100,
      })
        .then(() => {
          this.profilesLoaded = true;
        })
        .catch(() => {
          this.profileFetchFailed = true;
        })
        .finally(() => {
          this.profilesLoading = false;
        });
    },

    createDirectLink() {
      if (!this.$refs.direct_link_form.validate()) return;

      this.isLoading = true;

      this.CREATE_GUEST_LINK({
        project_id: this.projectId,
        name: this.title.trim(),
        profile_id: this.selectedProfileId,
        project_key_id: this.clientKey,
        default_language: this.defaultLanguage,
      })
        .then(async response => {
          const token = response?.data?.token;

          if (!token) {
            throw new Error("Guest link token was not returned");
          }

          this.closeDirectLinkModal();
          this.ADD_ALERT({
            message:
              response?.data?.message ||
              this.$vuetify.lang.t("$vuetify.ask_maira_access.guest_url_created_success_txt"),
            color: "success",
          });
          await this.getDirectLinks();
        })
        .catch(error => {
          this.ADD_ALERT({
            message:
              error.response?.data?.message ||
              this.$vuetify.lang.t("$vuetify.ask_maira_access.direct_link_create_failed"),
            color: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    updateDirectLink(status = null) {
      if (!status && !this.$refs.direct_link_form.validate()) return;

      this.isLoading = true;

      const payload = {
        project_id: this.projectId,
        link_id: this.selectedDirectLink.id,
        status: status || this.selectedDirectLink.status || "active",
        ...(!status && {
          name: this.title.trim(),
          profile_id: this.selectedProfileId,
          project_key_id: this.clientKey,
          default_language: this.defaultLanguage,
        }),
      };

      this.UPDATE_GUEST_LINK(payload)
        .then(async response => {
          this.ADD_ALERT({
            message: status
              ? status === "inactive"
                ? this.$vuetify.lang.t("$vuetify.ask_maira_access.guest_url_disabled")
                : this.$vuetify.lang.t("$vuetify.ask_maira_access.guest_url_enabled")
              : response?.data?.message,
            color: "success",
          });

          this.closeDirectLinkModal();
          await this.getDirectLinks();
        })
        .catch(error => {
          this.ADD_ALERT({
            message:
              error.response?.data?.message ||
              this.$vuetify.lang.t("$vuetify.ask_maira_access.direct_link_update_failed"),
            color: "error",
          });
        })
        .finally(() => {
          this.isLoading = false;
        });
    },

    mapClientKeyOption(credential, index) {
      if (typeof credential === "string") {
        return {
          name: `Client key ${index + 1}`,
          value: credential,
          status: "active",
        };
      }

      const value = credential?.id;

      return {
        name: credential?.name || credential?.title || `Client key ${index + 1}`,
        value,
        status: credential?.status || "active",
      };
    },

    isClientCredential(credential) {
      if (typeof credential === "string") return true;

      const keyType = credential?.key_type;

      return !keyType || keyType === "client";
    },

    mapGuestLink(item) {
      const profile = this.profileOptions.find(profileItem => profileItem.id === item.profile_id);
      const clientKey = this.findClientKeyOption(item.project_key_id);
      const token = item.token || "";

      return {
        ...item,
        profile: profile?.name || "N/A",
        client: this.getClientKeyName(item.project_key_id),
        default_language_name: this.getDefaultLanguageName(item.default_language),
        direct_link: token ? this.buildDirectLinkSilent(token, item.default_language) : "",
        client_key: item.project_key_id,
        profileMissing: !profile,
        clientKeyMissing: !clientKey || clientKey.status === "inactive",
      };
    },

    findClientKeyOption(projectKeyId) {
      return this.allClientKeyOptions.find(item => item.value === projectKeyId);
    },

    getClientKeyName(projectKeyId) {
      const clientKey = this.findClientKeyOption(projectKeyId);
      if (!clientKey) return "N/A";

      if (clientKey.status !== "inactive") return clientKey.name;

      const statusLabel = this.$vuetify.lang.t("$vuetify.ask_maira_access.inactive_status_txt");

      return `${clientKey.name} (${statusLabel})`;
    },

    getDefaultLanguageName(language) {
      const option = this.defaultLanguageOptions.find(item => item.value === language);

      return option?.name || language || "N/A";
    },

    getMissingActionMessage(item) {
      if (item.clientKeyMissing && item.profileMissing) {
        return this.$vuetify.lang.t("$vuetify.ask_maira_access.client_key_and_profile_missing_txt");
      }
      if (item.clientKeyMissing) {
        return this.$vuetify.lang.t("$vuetify.ask_maira_access.client_key_missing_txt");
      }
      if (item.profileMissing) {
        return this.$vuetify.lang.t("$vuetify.ask_maira_access.profile_missing_txt");
      }
      return "";
    },

    getChatPath(language) {
      const isSupportedLanguage = this.defaultLanguageOptions.some(option => option.value === language);

      return isSupportedLanguage ? `/${language}/chat` : "/chat";
    },

    buildDirectLinkSilent(token, language) {
      if (!this.MairaAppUrl) return "";
      try {
        const url = new URL(this.getChatPath(language), this.MairaAppUrl);
        url.searchParams.set("token", token);
        return url.toString();
      } catch (error) {
        return "";
      }
    },

    async copyToClipboard(text) {
      const copied = await copyTextToClipboard(text);
      this.ADD_ALERT({
        message: copied
          ? this.$vuetify.lang.t("$vuetify.ask_maira_access.copy_success")
          : this.$vuetify.lang.t("$vuetify.ask_maira_access.copy_failed"),
        color: copied ? "success" : "error",
      });
    },
    truncateDropdownText(text) {
      if (!text) return "N/A";

      const limit = this.dropdownItemNameLimit;

      return text.length > limit ? `${text.substring(0, limit)}...` : text;
    },

    dropdownTooltip(text) {
      if (!text) return "";

      return text.length > this.dropdownItemNameLimit ? text : "";
    },

    cancelDirectLinkAccessTypeChange() {
      this.accessTypeConfirmDialog = false;
      this.pendingDirectLinkAccessType = null;
      this.directLinkAccessType = this.previousDirectLinkAccessType;
    },

    async onDirectLinkAccessTypeChange() {
      if (!this.pendingDirectLinkAccessType || this.isLoadingAccessTypes) {
        return;
      }
      const accessType = this.pendingDirectLinkAccessType;
      try {
        this.isLoadingAccessTypes = true;
        const response = await this.UPDATE_PROJECT_DIRECT_LINK_ACCESS_TYPE({
          project_id: this.projectId,
          guest_link_auth_mode: accessType,
        });
        this.directLinkAccessType = accessType;
        this.previousDirectLinkAccessType = accessType;
        this.ADD_ALERT({
          message:
            response?.data?.message ||
            this.$vuetify.lang.t("$vuetify.ask_maira_access.change_access_type_success_message"),
          color: "success",
        });
      } catch (error) {
        console.error("Failed to update direct access link access type:", error);
        this.ADD_ALERT({
          message: getApiErrorMessage(error, "Failed to update access type"),
          color: "error",
        });
        this.directLinkAccessType = this.previousDirectLinkAccessType;
      } finally {
        this.accessTypeConfirmDialog = false;
        this.pendingDirectLinkAccessType = null;
        this.isLoadingAccessTypes = false;
      }
    },

    handleAccessTypeChange(accessType) {
      this.pendingDirectLinkAccessType = accessType;
      this.accessTypeConfirmDialog = true;
    },

    openResetPasswordModal(item) {
      this.selectedResetPasswordUser = item;
      this.resetPasswordConfirmDialog = true;
    },

    async confirmResetPassword() {
      this.resetPasswordConfirmDialog = false;
      if (!this.selectedResetPasswordUser) {
        return;
      }
      this.resetPasswordLoading = true;
      this.resetPasswordDialog = true;
      this.resetPasswordData = null;

      try {
        const response = await this.RESET_GUEST_LINK_USER_PASSWORD({
          project_id: this.projectId,
          user_id: this.selectedResetPasswordUser.id,
        });
        this.resetPasswordData = response?.data || {};
        this.ADD_ALERT({
          message:
            response?.data?.message || this.$vuetify.lang.t("$vuetify.ask_maira_access.reset_password_success_message"),
          color: "success",
        });
      } catch (error) {
        console.error("Failed to reset guest link user password:", error);
        this.resetPasswordDialog = false;
        this.ADD_ALERT({
          message: getApiErrorMessage(error, "Failed to reset password"),
          color: "error",
        });
      } finally {
        this.resetPasswordLoading = false;
      }
    },

    async copyResetPassword() {
      if (!this.resetPasswordData?.new_password) {
        return;
      }

      try {
        await copyTextToClipboard(this.resetPasswordData.new_password);
        this.reset_password_copied = true;

        setTimeout(() => {
          this.reset_password_copied = false;
        }, 2000);
      } catch (error) {
        console.error("Failed to copy password:", error);
        this.ADD_ALERT({
          message: this.$vuetify.lang.t("$vuetify.ask_maira_access.clipboard_not_available"),
          color: "error",
        });
      }
    },

    downloadResetPassword() {
      if (!this.selectedResetPasswordUser?.username || !this.resetPasswordData?.new_password) {
        return;
      }

      const data = {
        username: this.selectedResetPasswordUser.username,
        password: this.resetPasswordData.new_password,
        created_at: formatDateTime(new Date().toISOString()),
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: "application/json;charset=utf-8",
      });

      const url = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = `guest-link-credentials-${this.selectedResetPasswordUser.username}.json`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    async handleDirectLinkPageChange(page) {
      this.directLinkUsersPagination.page = page;
      await this.getDirectLinkUsers();
    },

    async handleDirectLinkItemsPerPageChange(itemsPerPage) {
      this.directLinkUsersPagination.itemsPerPage = itemsPerPage;
      this.directLinkUsersPagination.page = 1;
      await this.getDirectLinkUsers();
    },

    async handleDirectLinkSortByChange(sortBy) {
      if (!sortBy?.length) {
        if (
          this.directLinkUsersPagination.sortBy[0] == "created_at" &&
          this.directLinkUsersPagination.sortDesc[0] == true
        ) {
          this.directLinkUsersPagination.sortDesc = [false];
        } else {
          this.directLinkUsersPagination.sortDesc = [true];
        }
        this.directLinkUsersPagination.sortBy = ["created_at"];

        await this.getDirectLinkUsers();
      } else {
        if (sortBy[0] != this.directLinkUsersPagination.sortBy[0] && !this.directLinkUsersPagination.sortDesc[0]) {
          this.directLinkUsersPagination.sortBy = [...sortBy];

          await this.getDirectLinkUsers();
        } else {
          this.directLinkUsersPagination.sortBy = [...sortBy];
        }
      }
    },

    async handleDirectLinkSortDescChange(sortDesc) {
      this.directLinkUsersPagination.sortDesc = [...sortDesc];
      await this.getDirectLinkUsers();
    },

    closeResetPasswordDialog() {
      this.resetPasswordDialog = false;
      this.resetPasswordData = null;
      this.selectedResetPasswordUser = null;
    },
  },
};
</script>

<style scoped>
.direct-link-username-mobile {
  overflow-wrap: anywhere;
}

.direct-link-actions {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
}

::v-deep .direct-link-actions .v-btn {
  height: 28px;
  width: 28px;
  margin: 0;
}

.info-icon {
  display: inline-block;
  width: 18px;
  height: 18px;
  line-height: 18px;
  text-align: center;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #333;
  font-size: 12px;
  font-weight: bold;
  cursor: pointer;
  margin-left: 8px;
  pointer-events: auto;
}

.dropdown-item-text {
  display: block;
  width: 100%;
  max-width: 360px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.access-type-selector {
  width: 150px;
  max-width: 150px;
}
.reset-password-alert {
  word-break: break-word;
}

.reset-password-field {
  width: 100%;
}
@media (max-width: 600px) {
  .access-type-selector {
    width: 120px;
    max-width: 120px;
  }
  .download-password-btn {
    width: 100%;
  }
}
</style>
